/**
 * Migration API client.
 *
 * Reads and executes the one-time upgrade from a pre-v2 `config.yaml`
 * (`agents:` section) to the SQLite-backed panel state. Hit once on
 * first Dashboard mount per session — §9 / §10 of DEVELOPMENT_PLAN.md.
 *
 * Endpoints (delivered by Subagent 3 on the backend):
 *   - GET  /api/system/migration-status
 *   - POST /api/system/migrate-legacy           (step-up required)
 *
 * `getMigrationStatus` is side-effect free and cheap; `migrateLegacy`
 * mutates the server state (moves config.yaml → config.yaml.pre-v2 and
 * inserts rows into the `agents` + `agent_channels` tables).
 */
import { api } from '@/api/client';

/**
 * A single legacy agent parsed out of the old `config.yaml`. The bot
 * token is masked (e.g. `MTIz…xyz`) — the raw secret never leaves the
 * server.
 */
export interface LegacyAgent {
  name: string;
  description: string;
  discord_bot_token_masked: string;
  user_ids: string[];
  channel_ids: string[];
}

/**
 * Shape of `GET /api/system/migration-status`.
 *
 * When `legacy_agents_detected === true && already_migrated === false`
 * the Dashboard auto-opens the MigrationDialog.
 */
export interface MigrationStatus {
  legacy_agents_detected: boolean;
  legacy_agents: LegacyAgent[];
  already_migrated: boolean;
}

/**
 * Shape of `POST /api/system/migrate-legacy`. The backend returns after
 * all agents have been imported (or at least attempted). `archived_config`
 * is the path to the saved `config.yaml.pre-v2` file on disk.
 */
export interface MigrationResult {
  imported: number;
  failed: Array<{ name: string; error: string }>;
  archived_config: string;
}

/** GET /api/system/migration-status — cheap, no side effects. */
export async function getMigrationStatus(): Promise<MigrationStatus> {
  return api<MigrationStatus>('/system/migration-status');
}

/**
 * POST /api/system/migrate-legacy — runs the actual import.
 * Backend requires a step-up token; the helper forwards whatever
 * session cookies the browser already holds. If the stepup check
 * fails we let the ApiError propagate so the dialog can prompt again.
 */
export async function migrateLegacy(): Promise<MigrationResult> {
  return api<MigrationResult>('/system/migrate-legacy', { method: 'POST' });
}
